/**
 * Iter 31.300 — Station Publisher panel.
 *
 * Six's reorg loop: (1) 31.297 consolidates every scattered MP3 into
 * `_Filed/radio_raw_pool/`. (2) Six creates named subsets by hand in
 * Drive at `_Filed/radio_raw_pool/subsets/<slug>/`. (3) THIS panel
 * promotes a subset into a live station — moves every MP3 into
 * `_Filed/radio_stations/<target>/tracks/`. One tap. Idempotent.
 *
 * UI: subset dropdown (with per-subset track count) + target station
 * input (or existing station selector) + Dry-run/Publish buttons.
 * Backend clears the public station cache on commit so the new
 * station shows up on spyradio.uk without waiting for the TTL.
 */
import React, { useCallback, useEffect, useReducer } from "react";
import { api } from "@/lib/api";
import {
    Loader2, AlertTriangle, PlayCircle, Send, CheckCircle2,
    ExternalLink, Radio,
} from "lucide-react";

const INITIAL = {
    subsets: [],       // {slug, count, folder_id, link}
    stations: [],      // {slug, folder_id}
    loading: true,
    loadErr: null,
    subsetSlug: "",
    stationSlug: "",
    mode: "new",       // "new" or "existing"
    manifest: null,    // dry-run result
    manifestLoading: false,
    manifestErr: null,
    commitLoading: false,
    commitErr: null,
    commitResult: null,
};

function reducer(s, a) {
    switch (a.type) {
        case "load_ok": return { ...s, subsets: a.subsets, stations: a.stations, loading: false, loadErr: null };
        case "load_err": return { ...s, loading: false, loadErr: a.err };
        case "set_subset": return { ...s, subsetSlug: a.slug, manifest: null, commitResult: null };
        case "set_station_slug": return { ...s, stationSlug: a.slug, manifest: null };
        case "set_mode": return { ...s, mode: a.mode, stationSlug: "", manifest: null };
        case "manifest_busy": return { ...s, manifestLoading: true, manifestErr: null };
        case "manifest_ok": return { ...s, manifest: a.manifest, manifestLoading: false, manifestErr: null };
        case "manifest_err": return { ...s, manifestLoading: false, manifestErr: a.err };
        case "commit_busy": return { ...s, commitLoading: true, commitErr: null };
        case "commit_ok": return { ...s, commitLoading: false, commitResult: a.result, manifest: null };
        case "commit_err": return { ...s, commitLoading: false, commitErr: a.err };
        default: return s;
    }
}

// slugify → lowercase alnum + hyphens + underscores only.
function slugify(input) {
    return (input || "").toString().toLowerCase()
        .replace(/[^a-z0-9_-]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 64);
}

export default function StationPublisherPanel() {
    const [s, dispatch] = useReducer(reducer, INITIAL);

    const reload = useCallback(async () => {
        try {
            const [ssRes, stRes] = await Promise.all([
                api.get("/admin/radio/subsets"),
                api.get("/admin/radio/stations"),
            ]);
            dispatch({
                type: "load_ok",
                subsets: ssRes.data?.subsets || [],
                stations: stRes.data?.stations || [],
            });
        } catch (e) {
            dispatch({
                type: "load_err",
                err: `[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`,
            });
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const targetSlug = s.mode === "new" ? slugify(s.stationSlug) : s.stationSlug;
    const canRun = !!s.subsetSlug && !!targetSlug;

    const dryRun = useCallback(async () => {
        dispatch({ type: "manifest_busy" });
        try {
            const { data } = await api.post("/admin/radio/publish-station", {
                subset_slug: s.subsetSlug,
                station_slug: targetSlug,
                dry_run: true,
            });
            dispatch({ type: "manifest_ok", manifest: data });
        } catch (e) {
            dispatch({
                type: "manifest_err",
                err: `[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`,
            });
        }
    }, [s.subsetSlug, targetSlug]);

    const commit = useCallback(async () => {
        if (!s.manifest) return;
        const msg = (
            `Publish ${s.manifest.source_count} track(s) to station "${targetSlug}"?\n\n` +
            "This moves every MP3 in the subset into the station's tracks/ folder. " +
            "Idempotent — safe to re-run."
        );
        if (!window.confirm(msg)) return;
        dispatch({ type: "commit_busy" });
        try {
            const { data } = await api.post("/admin/radio/publish-station", {
                subset_slug: s.subsetSlug,
                station_slug: targetSlug,
                dry_run: false,
            });
            dispatch({ type: "commit_ok", result: data });
            reload();
        } catch (e) {
            dispatch({
                type: "commit_err",
                err: `[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`,
            });
        }
    }, [s.manifest, s.subsetSlug, targetSlug, reload]);

    return (
        <div
            className="paper-card border-2 border-ink/30 p-5 mb-6"
            data-testid="station-publisher-panel"
        >
            <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading uppercase tracking-[0.22em] text-sm text-ink flex items-center gap-2">
                    <Radio className="w-4 h-4 text-stamp-red" />
                    Station Publisher
                </h2>
                <button
                    type="button"
                    onClick={reload}
                    disabled={s.loading}
                    data-testid="sp-refresh"
                    className="font-heading uppercase tracking-[0.16em] text-[10px] text-ink-soft hover:text-stamp-red disabled:opacity-50"
                >
                    {s.loading ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "refresh"}
                </button>
            </div>

            <div className="font-body text-[11px] text-ink-soft mb-3 leading-relaxed">
                Iter 31.300 — promote a triaged subset from{" "}
                <code className="font-mono">_Filed/radio_raw_pool/subsets/&lt;slug&gt;/</code>{" "}
                into a live station folder{" "}
                <code className="font-mono">_Filed/radio_stations/&lt;target&gt;/tracks/</code>.
                Public station cache is cleared on commit so the new station appears on spyradio.uk immediately.
            </div>

            {s.loadErr ? (
                <div className="mb-3 label-typed text-[11px] leading-relaxed border-l-2 border-stamp-red pl-3 text-stamp-red flex items-start gap-1" data-testid="sp-load-err">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>{s.loadErr}</span>
                </div>
            ) : null}

            {/* Subset picker */}
            <div className="mb-3">
                <label className="font-body text-[10px] text-ink-soft uppercase tracking-wider mb-1 block">
                    1. Source subset
                </label>
                {s.subsets.length === 0 ? (
                    <div className="border border-dashed border-ink/25 p-3 font-body text-[11px] text-ink-faint italic" data-testid="sp-subsets-empty">
                        No subsets yet. Create folders under{" "}
                        <code className="font-mono">_Filed/radio_raw_pool/subsets/&lt;slug&gt;/</code>{" "}
                        in Drive and drop your triaged picks in.
                    </div>
                ) : (
                    <select
                        value={s.subsetSlug}
                        onChange={(e) => dispatch({ type: "set_subset", slug: e.target.value })}
                        data-testid="sp-subset-select"
                        className="w-full px-3 py-2 border border-ink/30 bg-paper-light font-mono text-[12px] text-ink"
                    >
                        <option value="">— pick a subset —</option>
                        {s.subsets.map((ss) => (
                            <option key={ss.slug} value={ss.slug}>
                                {ss.slug} · {ss.count} track{ss.count === 1 ? "" : "s"}
                            </option>
                        ))}
                    </select>
                )}
            </div>

            {/* Target picker */}
            <div className="mb-3">
                <label className="font-body text-[10px] text-ink-soft uppercase tracking-wider mb-1 block">
                    2. Target station
                </label>
                <div className="flex items-center gap-2 mb-2">
                    <button
                        type="button"
                        onClick={() => dispatch({ type: "set_mode", mode: "new" })}
                        data-testid="sp-mode-new"
                        className={`font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1 border ${s.mode === "new" ? "border-stamp-red text-stamp-red bg-stamp-red/5" : "border-ink/30 text-ink-soft hover:border-ink"}`}
                    >
                        New station
                    </button>
                    <button
                        type="button"
                        onClick={() => dispatch({ type: "set_mode", mode: "existing" })}
                        data-testid="sp-mode-existing"
                        className={`font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1 border ${s.mode === "existing" ? "border-stamp-red text-stamp-red bg-stamp-red/5" : "border-ink/30 text-ink-soft hover:border-ink"}`}
                    >
                        Existing station
                    </button>
                </div>
                {s.mode === "new" ? (
                    <input
                        type="text"
                        value={s.stationSlug}
                        onChange={(e) => dispatch({ type: "set_station_slug", slug: e.target.value })}
                        placeholder="e.g. late-night-jazz"
                        data-testid="sp-new-slug"
                        className="w-full px-3 py-2 border border-ink/30 bg-paper-light font-mono text-[12px] text-ink"
                    />
                ) : (
                    <select
                        value={s.stationSlug}
                        onChange={(e) => dispatch({ type: "set_station_slug", slug: e.target.value })}
                        data-testid="sp-existing-select"
                        className="w-full px-3 py-2 border border-ink/30 bg-paper-light font-mono text-[12px] text-ink"
                    >
                        <option value="">— pick a station —</option>
                        {s.stations.map((st) => (
                            <option key={st.slug} value={st.slug}>{st.slug}</option>
                        ))}
                    </select>
                )}
                {s.mode === "new" && s.stationSlug && targetSlug !== s.stationSlug ? (
                    <div className="font-body text-[10px] text-ink-faint italic mt-1">
                        Will be saved as <code className="font-mono text-stamp-red">{targetSlug}</code>
                    </div>
                ) : null}
            </div>

            {/* Manifest */}
            {s.manifest ? (
                <div
                    className="border border-stamp-red/40 bg-stamp-red/5 p-3 mb-3"
                    data-testid="sp-manifest"
                >
                    <div className="label-typed text-[10px] text-stamp-red mb-2">
                        Dry-run manifest
                    </div>
                    <div className="font-body text-[11px] text-ink-soft mb-2">
                        Will move{" "}
                        <span className="font-mono text-stamp-red font-bold" data-testid="sp-manifest-count">{s.manifest.source_count}</span>{" "}
                        track(s) from <code className="font-mono">{s.manifest.subset_slug}</code>{" "}
                        into <code className="font-mono">{s.manifest.station_slug}/tracks/</code>
                    </div>
                    {s.manifest.sample?.length ? (
                        <details>
                            <summary className="font-body text-[10px] text-ink-soft cursor-pointer hover:text-stamp-red">
                                First {s.manifest.sample.length} tracks…
                            </summary>
                            <ul className="mt-1 max-h-40 overflow-y-auto font-mono text-[10px] text-ink-soft space-y-0.5">
                                {s.manifest.sample.map((n, i) => (
                                    <li key={i} className="truncate text-ink">{n}</li>
                                ))}
                            </ul>
                        </details>
                    ) : null}
                </div>
            ) : null}

            {/* Commit result */}
            {s.commitResult ? (
                <div
                    className="border-2 border-emerald-700 bg-emerald-50 p-3 mb-3"
                    data-testid="sp-commit-result"
                >
                    <div className="label-typed text-[10px] text-emerald-800 flex items-center gap-1 mb-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Station published
                    </div>
                    <div className="font-body text-[11px] text-ink-soft">
                        Moved <span className="font-mono text-stamp-red font-bold">{s.commitResult.moved}</span> track(s)
                        into <code className="font-mono">{s.commitResult.station_slug}</code>.
                        {s.commitResult.station_link ? (
                            <>
                                {" "}
                                <a
                                    href={s.commitResult.station_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-stamp-red hover:underline inline-flex items-center gap-1"
                                >
                                    Open in Drive <ExternalLink className="w-3 h-3" />
                                </a>
                            </>
                        ) : null}
                    </div>
                </div>
            ) : null}

            {/* Errors */}
            {s.manifestErr ? (
                <div className="text-[11px] text-stamp-red font-body mb-2" data-testid="sp-manifest-err">
                    {s.manifestErr}
                </div>
            ) : null}
            {s.commitErr ? (
                <div className="text-[11px] text-stamp-red font-body mb-2" data-testid="sp-commit-err">
                    {s.commitErr}
                </div>
            ) : null}

            {/* Action row */}
            <div className="flex items-center gap-2 flex-wrap">
                <button
                    type="button"
                    onClick={dryRun}
                    disabled={!canRun || s.manifestLoading || s.commitLoading}
                    data-testid="sp-dry-run"
                    className="font-heading uppercase text-[10px] tracking-[0.22em] px-4 py-2 border-2 border-ink/40 text-ink hover:border-stamp-red hover:text-stamp-red disabled:opacity-40 disabled:hover:border-ink/40 disabled:hover:text-ink inline-flex items-center gap-1.5"
                >
                    {s.manifestLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />}
                    Dry-run
                </button>
                <button
                    type="button"
                    onClick={commit}
                    disabled={!s.manifest || s.commitLoading || s.manifestLoading}
                    data-testid="sp-publish"
                    className="font-heading uppercase text-[10px] tracking-[0.22em] px-4 py-2 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red/10 disabled:opacity-40 disabled:hover:bg-transparent inline-flex items-center gap-1.5"
                >
                    {s.commitLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                    Publish station
                </button>
            </div>
        </div>
    );
}
